import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  KeyRound,
  LoaderCircle,
  Mail
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { AuthPanel, FormError, IdentityShell } from "../components/IdentityShell.tsx";
import {
  auth,
  enterpriseSsoConfiguration,
  safeAccountSetupReturnTo,
  safeReturnTo
} from "../lib/auth-client.ts";

const enterpriseSso = enterpriseSsoConfiguration();
type LoginMode = "login" | "recovery" | "recovery-sent";

export function Login() {
  const query = new URLSearchParams(window.location.search);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<LoginMode>(
    query.get("mode") === "recovery" ? "recovery" : "login"
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const returnTo = safeReturnTo(query.get("return_to"));
  const accountSetupReturnTo = safeAccountSetupReturnTo(query.get("return_to"));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const result = await auth.signInWithPassword(email, password);
    setBusy(false);
    if (result.error) {
      setError("账号或密码不正确，请检查后重试。");
      return;
    }
    window.location.assign(returnTo);
  }

  async function requestRecovery(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const completionUrl = new URL("/auth/complete", window.location.origin);
    completionUrl.searchParams.set("flow", "recovery");
    if (accountSetupReturnTo) {
      completionUrl.searchParams.set("return_to", accountSetupReturnTo);
    }
    const result = await auth.requestPasswordRecovery(
      email.trim().toLowerCase(),
      completionUrl.toString()
    );
    setBusy(false);
    if (result.error) {
      setError("账号设置邮件暂时无法发送，请稍后再试。");
      return;
    }
    setMode("recovery-sent");
  }

  async function sso() {
    if (busy) return;
    setError(null);
    sessionStorage.setItem("matrix-auth-return-to", returnTo);
    const redirectTo = `${window.location.origin}/auth/callback`;
    if (enterpriseSso.mode === "saml") {
      const result = await auth.signInWithSaml(enterpriseSso.domain, redirectTo);
      if (result.error || !result.data?.url) {
        setError("企业单点登录暂不可用，请联系管理员。");
        return;
      }
      window.location.assign(result.data.url);
      return;
    }
    if (enterpriseSso.mode !== "oidc") {
      setError("企业单点登录暂不可用，请联系管理员。");
      return;
    }
    const result = await auth.signInWithOAuth(enterpriseSso.provider, redirectTo);
    if (result.error) setError("企业单点登录暂不可用，请联系管理员。");
  }

  return (
    <IdentityShell
      step={mode === "login" ? "登录" : "设置"}
      status={mode === "recovery-sent" ? "邮件已请求" : "需要身份验证"}
    >
      <AuthPanel
        eyebrow="Matterline"
        title={
          mode === "login" ? "登录" : mode === "recovery" ? "重新发送账号设置邮件" : "检查邮箱"
        }
        lede={
          mode === "recovery"
            ? "输入开通账号时使用的邮箱，我们会发送一个新的安全链接。"
            : mode === "recovery-sent"
              ? "如果该邮箱已开通账号，新的账号设置邮件已发送。请以最新一封邮件为准。"
              : enterpriseSso.mode === "oidc"
                ? "仅受邀账号可登录。请使用密码或企业身份继续。"
                : "仅受邀账号可登录。请使用邀请邮件中设置的密码。"
        }
        footer={
          mode === "login"
            ? "登录即表示你在受控工作区内访问文档与知识库。"
            : "出于安全考虑，页面不会显示该邮箱是否已开通账号。"
        }
      >
        {mode === "login" ? (
          <>
            <form onSubmit={submit} noValidate>
              <label htmlFor="auth-email">
                邮箱
                <input
                  id="auth-email"
                  autoComplete="email"
                  type="email"
                  inputMode="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  disabled={busy}
                />
              </label>
              <label htmlFor="auth-password">
                密码
                <input
                  id="auth-password"
                  autoComplete="current-password"
                  type="password"
                  placeholder="输入登录密码"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  disabled={busy}
                />
              </label>
              {error ? <FormError>{error}</FormError> : null}
              <button className="primary" disabled={busy} type="submit">
                {busy ? <LoaderCircle className="spin" size={17} /> : <KeyRound size={17} />}
                继续
                <ArrowRight size={17} />
              </button>
            </form>
            <button
              className="text-action recovery-action"
              type="button"
              disabled={busy}
              onClick={() => {
                setError(null);
                setMode("recovery");
              }}
            >
              <Mail size={15} />
              重新发送账号设置邮件
            </button>
            {enterpriseSso.mode !== "disabled" ? (
              <>
                <div className="divider">
                  <span>或</span>
                </div>
                <button
                  className="secondary"
                  type="button"
                  onClick={() => void sso()}
                  disabled={busy}
                >
                  <Building2 size={17} /> 企业单点登录
                </button>
              </>
            ) : null}
          </>
        ) : mode === "recovery" ? (
          <form onSubmit={requestRecovery}>
            <label htmlFor="recovery-email">
              邮箱
              <input
                id="recovery-email"
                autoFocus
                autoComplete="email"
                type="email"
                inputMode="email"
                placeholder="name@company.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                disabled={busy}
              />
            </label>
            {error ? <FormError>{error}</FormError> : null}
            <button className="primary" disabled={busy} type="submit">
              {busy ? <LoaderCircle className="spin" size={17} /> : <Mail size={17} />}
              发送新邮件
              <ArrowRight size={17} />
            </button>
            <button
              className="text-action recovery-back"
              type="button"
              disabled={busy}
              onClick={() => {
                setError(null);
                setMode("login");
              }}
            >
              <ArrowLeft size={15} /> 返回登录
            </button>
          </form>
        ) : (
          <div className="recovery-sent" role="status" aria-live="polite">
            <CheckCircle2 size={22} aria-hidden="true" />
            <button className="secondary" type="button" onClick={() => setMode("login")}>
              <ArrowLeft size={17} /> 返回登录
            </button>
          </div>
        )}
      </AuthPanel>
    </IdentityShell>
  );
}
